import { useState } from "react";
import { useAccount, useConnect, useChainId, useWriteContract, useSendTransaction } from 'wagmi';
import { parseEther, parseUnits } from 'viem';
import { RECEIVER, USDC_BASE } from '../prices';
import AddressInput from "./AddressInput";
// import { encodeFunctionData, parseEther, parseUnits, numberToHex, createPublicClient, http, toHex } from 'viem';
// import { base as baseChain } from 'viem/chains';

// CSS para animação da estrela
const starAnimationCSS = `
  @keyframes rotateRight {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

// ABI mínimo para ERC20.transfer(address,uint256)
/* const erc20Abi = [
  {
    type: "function",
    name: "transfer",
    stateMutability: "nonpayable",
    inputs: [
      { name: "to", type: "address" },
      { name: "value", type: "uint256" }
    ],
    outputs: [{ name: "", type: "bool" }]
  }
] as const; */

type Address = {
  name: string; email: string;
  address_line1: string; address_line2?: string;
  city: string; region: string; postal_code: string; country: string;
  place_id?: string; lat?: number; lng?: number;
};

export default function DeliveryFlow({ 
  onClose, 
  onPayEth, 
  onPayUsdc 
}: { 
  onClose?: () => void;
  onPayEth?: (qty: number, mode: 'delivery'|'parks') => void;
  onPayUsdc?: (qty: number, mode: 'delivery'|'parks') => void;
}) {
  type Step = "choice" | "shipping" | "payNo";
  const [step, setStep] = useState<Step>("choice");
  const [qty, setQty] = useState(1);
  const [loading, setLoading] = useState<"USDC" | "ETH" | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  
  // Wagmi hooks (Mini App connector)
  const { isConnected, address: wagmiAddress } = useAccount();
  const { connect, connectors } = useConnect();
  const chainId = useChainId();
  const { writeContractAsync } = useWriteContract();
  const { sendTransactionAsync } = useSendTransaction();
  
  // Função para adicionar logs visuais - REMOVIDA

  // snapshot do endereço (YES)
  const [addr, setAddr] = useState<Address>({
    name: "", email: "",
    address_line1: "", address_line2: "",
    city: "", region: "", postal_code: "", country: "",
  });

  const inc = () => setQty(q => Math.min(20, q + 1));
  const dec = () => setQty(q => Math.max(1, q - 1));

  // Função de validação dos campos obrigatórios
  const validateForm = (): boolean => {
    const errors: Partial<Record<keyof Address, string>> = {};
    
    // Helper para verificar se um campo está vazio (trata null, undefined e strings vazias)
    const isEmpty = (value: string | undefined | null): boolean => {
      return !value || typeof value !== 'string' || !value.trim();
    };
    
    // CORREÇÃO AUTOMÁTICA: Se address_line1 está vazio mas address_line2 tem conteúdo,
    // provavelmente o usuário digitou no campo errado. Mover o valor automaticamente.
    let workingAddr = { ...addr };
    if (isEmpty(addr.address_line1) && !isEmpty(addr.address_line2)) {
      console.log('⚠️ address_line1 vazio mas address_line2 tem conteúdo - movendo automaticamente');
      console.log('📝 address_line2 atual:', addr.address_line2);
      workingAddr.address_line1 = addr.address_line2 || '';
      workingAddr.address_line2 = '';
      // Atualizar o estado imediatamente
      setAddr(workingAddr);
      console.log('✅ Valor movido: address_line1 agora é', workingAddr.address_line1);
    }
    
    // Validação de cada campo com logs individuais
    if (isEmpty(workingAddr.name)) {
      errors.name = "Nome é obrigatório";
      console.log('❌ Campo name está vazio:', workingAddr.name);
    }
    if (isEmpty(workingAddr.email)) {
      errors.email = "Email é obrigatório";
      console.log('❌ Campo email está vazio:', workingAddr.email);
    } else if (workingAddr.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(workingAddr.email.trim())) {
      errors.email = "Email inválido";
      console.log('❌ Campo email é inválido:', workingAddr.email);
    }
    if (isEmpty(workingAddr.address_line1)) {
      errors.address_line1 = "Endereço é obrigatório";
      console.log('❌ Campo address_line1 está vazio:', workingAddr.address_line1);
    }
    if (isEmpty(workingAddr.city)) {
      errors.city = "Cidade é obrigatória";
      console.log('❌ Campo city está vazio:', workingAddr.city);
    }
    if (isEmpty(workingAddr.region)) {
      errors.region = "Estado/Província é obrigatório";
      console.log('❌ Campo region está vazio:', workingAddr.region);
    }
    if (isEmpty(workingAddr.postal_code)) {
      errors.postal_code = "CEP/ZIP é obrigatório";
      console.log('❌ Campo postal_code está vazio:', workingAddr.postal_code);
    }
    if (isEmpty(workingAddr.country)) {
      errors.country = "País é obrigatório";
      console.log('❌ Campo country está vazio:', workingAddr.country);
    }
    
    // Log completo para debug
    console.log('🔍 Validação do formulário COMPLETA (DeliveryFlow.tsx):', {
      addr: {
        name: `"${workingAddr.name}" (tipo: ${typeof workingAddr.name})`,
        email: `"${workingAddr.email}" (tipo: ${typeof workingAddr.email})`,
        address_line1: `"${workingAddr.address_line1}" (tipo: ${typeof workingAddr.address_line1})`,
        address_line2: `"${workingAddr.address_line2}" (tipo: ${typeof workingAddr.address_line2})`,
        city: `"${workingAddr.city}" (tipo: ${typeof workingAddr.city})`,
        region: `"${workingAddr.region}" (tipo: ${typeof workingAddr.region})`,
        postal_code: `"${workingAddr.postal_code}" (tipo: ${typeof workingAddr.postal_code})`,
        country: `"${workingAddr.country}" (tipo: ${typeof workingAddr.country})`,
      },
      errors,
      errorKeys: Object.keys(errors),
      isValid: Object.keys(errors).length === 0
    });
    
    return Object.keys(errors).length === 0;
  };

  const recipient: `0x${string}` = RECEIVER;

  function pay(currency: "USDC" | "ETH", wantsReceive: boolean) {
      console.log(`🚀 BOTÃO ${currency} CLICADO - Início do onClick`);
      console.log(`📊 wantsReceive: ${wantsReceive}`);
      console.log('📊 Estado atual do addr:', JSON.stringify(addr, null, 2));
      
      setLoading(currency);
      setMessage(null);
    console.log(`🎯 Botão ${currency} clicado! wantsReceive: ${wantsReceive}`);
    
    if (wantsReceive) {
      console.log('🔍 Modo delivery - chamando validateForm()...');
      const isValid = validateForm();
      console.log('✅ Resultado da validação:', isValid);
      
      if (!isValid) {
        console.log('❌ Validação falhou - mostrando mensagem de erro');
        setMessage({ type: 'error', text: 'Please fill out all required fields.' });
        setLoading(null);
        return;
      }
      console.log('✅ Validação passou - continuando com pagamento');
    } else {
      console.log('✅ Modo parks - sem validação de endereço');
    }
    
    // Usar as props de pagamento se disponíveis
    const mode = wantsReceive ? 'delivery' : 'parks';
    if (currency === 'ETH' && onPayEth) {
      onPayEth(qty, mode);
    } else if (currency === 'USDC' && onPayUsdc) {
      onPayUsdc(qty, mode);
    } else {
      // Fallback para o método antigo se as props não estiverem disponíveis
      executePayment(currency, wantsReceive);
    }
  }

  async function executePayment(currency: "USDC" | "ETH", wantsReceive: boolean) {
    try {
      const baseAmount = currency === "ETH" ? 0.005 : 15;
      const totalAmount = baseAmount * qty;
      console.log(`💰 Valor: ${baseAmount} ${currency} × ${qty} = ${totalAmount} ${currency}`);

      console.log(`🔌 Wagmi conectado: ${isConnected} addr: ${wagmiAddress || 'N/A'}`);
      if (!isConnected && connectors && connectors[0]) {
        console.log('🪪 Conectando via Mini App connector...');
        await connect({ connector: connectors[0] });
      }
      console.log(`📡 chainId (wagmi): ${chainId ? '0x' + chainId.toString(16) : 'unknown'}`);

      let hash: string | null = null;
      if (currency === 'ETH') {
        const value = parseEther(String(totalAmount));
        console.log(`📋 ETH → to: ${recipient}, value: ${value.toString()} wei, chainId: 0x2105`);
        hash = await sendTransactionAsync({ to: recipient, value });
      } else {
        const amount = parseUnits(String(totalAmount), 6);
        console.log(`📋 USDC → to: ${USDC_BASE}, value: 0x0, chainId: 0x2105, transfer(${recipient}, ${amount.toString()})`);
        hash = await writeContractAsync({
          address: USDC_BASE,
          abi: [
            { type: 'function', name: 'transfer', stateMutability: 'nonpayable', inputs: [ { name: 'to', type: 'address' }, { name: 'value', type: 'uint256' } ], outputs: [ { name: '', type: 'bool' } ] }
          ] as const,
          functionName: 'transfer',
          args: [recipient, amount]
        });
      }

      if (hash) {
        console.log(`✅ Tx enviada! Hash: ${hash}`);
        window.dispatchEvent(new CustomEvent('purchase:success', { 
          detail: { 
            orderId: 'success', 
            hash,
            source: 'farcaster',
            currency,
            qty,
            mode: wantsReceive ? 'delivery' : 'parks',
            address: wantsReceive ? { ...addr } : undefined
          } 
        }));
        setMessage({ type: 'success', text: 'Payment sent! Check your wallet.' });
      } else {
        setMessage({ type: 'error', text: 'Failed to send transaction.' });
      }
    } catch (e: any) {
      console.log(`❌ Erro ao enviar: ${e?.message || e}`);
      setMessage({ type: 'error', text: 'Transaction failed. Please try again.' });
    } finally {
      setLoading(null);
    }
  }

  // Pagamentos desabilitados: função mantida vazia para evitar referências
  // Removido: função de pagamento não usada enquanto pagamentos estão desabilitados
  // function pay(_currency: "USDC" | "ETH", _wantsReceive: boolean) {}
    
        // FUNÇÃO ASSÍNCRONA - executa em background
    // Removido: fluxo de pagamento enquanto pagamentos estão desabilitados
    // async function executePayment(currency: "USDC" | "ETH", wantsReceive: boolean) {
      /*
      try {
        addLog('🚀 executePayment iniciado...');
        let tx: string | null = null;
        let success = false;
        let sentAlready = false;
        
        // Verificar se estamos no Farcaster Mini App
        addLog('🔍 Verificando ambiente Farcaster...');
        addLog(`🔍 window.FarcasterFrameContext: ${typeof (window as any).FarcasterFrameContext}`);
        addLog(`🔍 window.location.href: ${window.location.href}`);
        addLog(`🔍 document.referrer: ${document.referrer}`);
        addLog(`🔍 navigator.userAgent: ${navigator.userAgent.substring(0, 100)}...`);
        
        // Detecção mais robusta do Mini App
        const isFarcasterMiniApp = 
          typeof (window as any).FarcasterFrameContext !== 'undefined' ||
          document.referrer.includes('warpcast') ||
          document.referrer.includes('farcaster') ||
          navigator.userAgent.includes('warpcast') ||
          window.location.href.includes('warpcast') ||
          window.location.href.includes('farcaster');
        
        addLog(`🔍 isFarcasterMiniApp: ${isFarcasterMiniApp}`);
        
        // Permitir qualquer carteira (Farcaster decide)
        addLog('🔍 Carteira disponível: deixar Farcaster decidir');
        
        if (isFarcasterMiniApp) {
          addLog('🎯 Farcaster Mini App detectado!');
          console.log('🎯 Farcaster Mini App - iniciando transação real...');
        
        try {
          // Calcular valor total baseado na quantidade selecionada
          const baseAmount = currency === "ETH" ? 0.005 : 15;
          const totalAmount = baseAmount * qty;
          
          addLog(`💰 Calculando valor: ${baseAmount} ${currency} × ${qty} = ${totalAmount} ${currency}`);
          console.log(`💰 Transação: ${totalAmount} ${currency} × ${qty} = ${totalAmount * qty}`);
          
          // Obter provider do Farcaster
          
          // Obter provider do Farcaster
          addLog('📱 Importando SDK do Farcaster...');
          const { sdk } = await import('@farcaster/miniapp-sdk');
          
          addLog('🔌 Obtendo provider da carteira nativa...');
          const provider = await sdk.wallet.getEthereumProvider();
          
          if (!provider) {
            addLog('❌ Provider da carteira nativa não disponível!');
            addLog('❌ Verifique se está usando a carteira nativa do Farcaster');
            throw new Error('Farcaster native wallet provider não disponível');
          }
          
          addLog('✅ Provider da carteira nativa obtido com sucesso!');
          addLog(`🔍 Provider type: ${typeof provider}`);
          addLog(`🔍 Provider methods: ${Object.keys(provider).join(', ')}`);
          console.log('📱 Provider Farcaster obtido:', provider);
          
          // Testar se o provider está funcionando
          addLog('🧪 Testando provider com eth_accounts...');
          try {
            const accounts = await provider.request({ method: 'eth_accounts' });
            addLog(`✅ eth_accounts funcionando: ${JSON.stringify(accounts)}`);
          } catch (testError: any) {
            addLog(`❌ eth_accounts falhou: ${testError.message}`);
            addLog('⚠️ Provider pode não estar funcionando corretamente');
          }

          // Garantir que estamos na Base Mainnet (8453 => 0x2105)
          const BASE_MAINNET = '0x2105';
          addLog('🧪 Checando chain com eth_chainId...');
          try {
            const currentChainId = await provider.request({ method: 'eth_chainId' });
            addLog(`🔗 chainId atual: ${currentChainId}`);
            if (currentChainId !== BASE_MAINNET) {
              addLog('🔄 Trocando para Base (8453)...');
              try {
                await provider.request({ method: 'wallet_switchEthereumChain', params: [{ chainId: BASE_MAINNET }] });
                addLog('✅ Troca para Base concluída');
              } catch (switchErr: any) {
                addLog(`❌ Falha ao trocar para Base: ${switchErr.message || switchErr}`);
              }
            }
          } catch (cidErr: any) {
            addLog(`⚠️ Falha ao obter chainId: ${cidErr.message || cidErr}`);
          }
          
          // Endereço do recebedor e contrato USDC (Base mainnet)
          const recipient = "0x4402764E3c27186D848C3E847D1358552939a090" as `0x${string}`;
          const USDC_BASE = "0x833589fCD6EDb6E08f4c7C32D4f71b54bdA02913" as `0x${string}`;

          // Descobrir account (from)
          const accounts = await provider.request({ method: 'eth_accounts' }) as `0x${string}`[];
          const from = accounts?.[0];
          if (!from) {
            throw new Error('Nenhuma conta conectada no provider');
          }

          // Criar transação
          addLog('📋 Criando transação...');
          let transaction: any;
          if (currency === 'ETH') {
            const ethAmountWei = parseEther(String(totalAmount));
            transaction = {
              from,
              to: recipient,
              value: numberToHex(ethAmountWei),
              data: '0x'
            };
          } else {
            // USDC via Wagmi (Mini App connector)
            const usdcAmount = parseUnits(String(totalAmount), 6);
            addLog(`🔗 Wagmi connected: ${isConnected} address: ${wagmiAddress || 'N/A'}`);
            addLog(`📡 chainId (wagmi): ${chainId ? '0x' + chainId.toString(16) : 'unknown'}`);

            try {
              if (!isConnected) {
                addLog('🪪 Conectando via Mini App connector...');
                if (connectors && connectors[0]) {
                  await connect({ connector: connectors[0] });
                } else {
                  addLog('⚠️ Nenhum connector disponível no Wagmi');
                }
              }

              addLog('🧮 Codificando chamada transfer(USDC)...');
              addLog(`📋 to: ${USDC_BASE}, value: 0x0, chainId: 0x2105`);
              addLog('🚀 Enviando via Wagmi writeContract (USDC transfer)...');

              const hash = await writeContractAsync({
                address: USDC_BASE as `0x${string}`,
                abi: erc20Abi as any,
                functionName: 'transfer',
                args: [recipient as `0x${string}`, usdcAmount],
              });

              addLog(`✅ Transação enviada via Wagmi! Hash: ${hash}`);
              tx = hash as string;
              success = true;
              sentAlready = true;

              // Aguardar receipt (mesma lógica)
              try {
                addLog('🧾 Aguardando transaction receipt...');
                let receipt: any = null;
                for (let i = 0; i < 30; i++) {
                  receipt = await provider.request({ method: 'eth_getTransactionReceipt', params: [tx as `0x${string}`] });
                  if (receipt) break;
                  await new Promise(r => setTimeout(r, 1000));
                }
                if (receipt) {
                  addLog(`✅ Receipt recebido: status=${receipt.status}`);
                } else {
                  addLog('⚠️ Receipt não disponível após 30s; seguindo com hash.');
                }
              } catch (recErr: any) {
                addLog(`⚠️ Falha ao aguardar receipt: ${recErr.message || recErr}`);
              }
            } catch (wagmiErr: any) {
              addLog(`❌ Wagmi writeContract falhou: ${wagmiErr?.message || wagmiErr}`);
              addLog('↩️ Fallback para provider.request eth_sendTransaction com fee injection...');

              // Fallback: provider.request com fee injection (como antes)
              const data = encodeFunctionData({
                abi: erc20Abi,
                functionName: 'transfer',
                args: [recipient, usdcAmount]
              });

              const baseChainIdHex = '0x2105';
              const txBase = {
                from,
                to: USDC_BASE,
                data: data as `0x${string}`,
                value: '0x0' as const,
                chainId: baseChainIdHex,
              };

              try {
                addLog('⛽ Coletando gas/fees via viem (feeHistory/gasPrice)...');
                const publicClient = createPublicClient({ chain: baseChain, transport: http() });
                const [gasEst, feeHist, gasPrice] = await Promise.allSettled([
                  publicClient.estimateGas(txBase as any),
                  publicClient.getFeeHistory({ blockCount: 1, rewardPercentiles: [50] }),
                  publicClient.getGasPrice(),
                ]);

                const gas = gasEst.status === 'fulfilled' ? toHex(gasEst.value) : undefined;
                if (gas) addLog(`⛽ estimateGas: ${gas}`); else addLog('⚠️ estimateGas falhou');

                let maxFeePerGas: string | undefined;
                let maxPriorityFeePerGas: string | undefined;
                if (feeHist.status === 'fulfilled') {
                  const baseFee = feeHist.value.baseFeePerGas.at(-1)!;
                  const tip = feeHist.value.reward?.[0]?.[0] ?? 100_000_000n; // ~0.1 gwei fallback
                  maxPriorityFeePerGas = toHex(tip);
                  maxFeePerGas = toHex(baseFee + tip);
                  addLog(`💸 feeHistory ok baseFee=${baseFee} tip=${tip}`);
                } else if (gasPrice.status === 'fulfilled') {
                  const tip = gasPrice.value / 10n; // ~10%
                  maxPriorityFeePerGas = toHex(tip);
                  maxFeePerGas = toHex(gasPrice.value + tip);
                  addLog(`💸 gasPrice ok gp=${gasPrice.value} tip=${tip}`);
                } else {
                  addLog('⚠️ Sem feeHistory e sem gasPrice');
                }

                transaction = {
                  ...txBase,
                  ...(gas ? { gas } : {}),
                  ...(maxFeePerGas && maxPriorityFeePerGas ? { maxFeePerGas, maxPriorityFeePerGas } : {}),
                  type: '0x2',
                } as any;
              } catch (feeErr: any) {
                addLog(`⚠️ Erro coletando gas/fees: ${feeErr?.message || feeErr}`);
                transaction = { ...txBase } as any;
              }
            }
          }

          if (transaction) {
            addLog(`📋 Transação criada: from=${from}, to=${transaction.to}, value=${transaction.value}, data=${transaction.data}`);
            console.log('📋 Transação criada:', transaction);
          }

          // Revert: não usar wallet_sendCalls; vamos enviar via eth_sendTransaction para USDC também
          
          // Enviar via provider quando não usamos Wagmi já
          if (!sentAlready && transaction) {
            addLog('🚀 Enviando transação via provider...');
            addLog(`📋 Método: eth_sendTransaction`);
            addLog(`📋 Params: ${JSON.stringify([transaction])}`);
            try {
              const result = await provider.request({
                method: 'eth_sendTransaction',
                params: [transaction]
              });
              addLog(`✅ Transação enviada! Hash: ${result}`);
              addLog(`✅ Tipo do resultado: ${typeof result}`);
              addLog(`✅ Resultado completo: ${JSON.stringify(result)}`);
              console.log('✅ Transação enviada via Farcaster:', result);
              tx = result as string;
              success = true;

              // Aguardar receipt para garantir finalização antes de passos seguintes
              try {
                addLog('🧾 Aguardando transaction receipt...');
                let receipt: any = null;
                for (let i = 0; i < 30; i++) {
                  receipt = await provider.request({ method: 'eth_getTransactionReceipt', params: [tx as `0x${string}`] });
                  if (receipt) break;
                  await new Promise(r => setTimeout(r, 1000));
                }
                if (receipt) {
                  addLog(`✅ Receipt recebido: status=${receipt.status}`);
                } else {
                  addLog('⚠️ Receipt não disponível após 30s; seguindo com hash.');
                }
              } catch (recErr: any) {
                addLog(`⚠️ Falha ao aguardar receipt: ${recErr.message || recErr}`);
              }
            } catch (txRequestError: any) {
              addLog(`❌ Erro na requisição: ${txRequestError.message || txRequestError}`);
              addLog(`❌ Código do erro: ${txRequestError.code || 'N/A'}`);
              addLog(`❌ Stack trace: ${txRequestError.stack || 'N/A'}`);
              throw txRequestError;
            }
          }
          
        } catch (txError: any) {
          addLog(`❌ Erro na transação: ${txError.message || txError}`);
          console.error('❌ Erro na transação Farcaster:', txError);
          
          // Fallback para transação mock se falhar
          addLog('⚠️ Usando fallback mock...');
          console.log('⚠️ Usando fallback mock...');
          tx = null;
          success = false;
          
        setMessage({
          type: 'error',
            text: 'Transaction failed. Please check your wallet and try again.'
          });
        }
      } else {
        // Não é Mini App - usar transação mock
        addLog('🖥️ Browser normal detectado - usando transação mock');
        console.log('🖥️ Browser normal - usando transação mock');
        tx = null;
        success = false;
      }

      // Chamar onPaid para fechar o modal
      if (success && tx) {
        addLog('✅ Fluxo concluído com sucesso — registrando compra');
      await onPaid?.({
        currency,
        qty,
        tx,
        address: wantsReceive ? {
          ...addr,
          // Incluir dados extras do Google Places se disponível
          place_id: googleAddressData?.place_id,
          lat: googleAddressData?.lat,
          lng: googleAddressData?.lng
        } : undefined,
      });

      // Disparar evento para abrir popup de sucesso
      window.dispatchEvent(new CustomEvent('purchase:success', { 
        detail: { 
          orderId: 'success', 
          hash: tx,
          currency,
          qty,
          address: wantsReceive ? {
            ...addr,
            // Incluir dados extras do Google Places se disponível
            place_id: googleAddressData?.place_id,
            lat: googleAddressData?.lat,
            lng: googleAddressData?.lng
          } : undefined
        } 
      }));
      } else {
        addLog('⏭️ Sem transação confirmada — não vai abrir popup de sucesso nem registrar.');
      }

      // Modal será fechado pelo onPaid
    } catch (e: any) {
      // Feedback de erro
      setMessage({
        type: 'error',
        text: 'Oh, boy! Something went wrong. Please make sure you have enough funds in your wallet and try again. If the issue persists, please email walt@sneakymouse.wtf.'
      });
    } finally {
      setLoading(null);
    }
  }
  */
  
  // HUD de logs para debug - REMOVIDO DO MINI APP
  // const LogsHUD = () => { ... };
  // const LogsHUDTop = () => { ... };

  // Mini App / Base App keep the on-chain modal on every viewport.
  
  if (step === "choice") {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.9)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
        padding: '20px'
      }}>
        {/* HUD topo global - REMOVIDO */}
        <style>
          {`
            @keyframes rotateRight {
              from { transform: rotate(0deg); }
              to { transform: rotate(360deg); }
            }
          `}
        </style>
        <div style={{
          position: 'relative',
          maxWidth: '500px',
          width: '100%'
        }}>
          
          {/* HUD de logs para debug - REMOVIDO */}
          {/* Botão X para fechar */}
          <button
            onClick={onClose}
            style={{
              position: 'absolute',
              top: '-35px',
              right: '24px',
              background: 'none',
              border: 'none',
              fontSize: '28px',
              cursor: 'pointer',
              color: '#ffffff',
              padding: '8px',
              borderRadius: '50%',
              width: '40px',
              height: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background-color 0.2s ease'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            ×
          </button>

          {/* Título principal */}
          <div style={{
            textAlign: 'center',
            marginBottom: '20px',
            marginTop: '20px',
            fontFamily: 'Chalkboard, sans-serif'
          }}>
                                      <h1 style={{
               fontSize: '32px',
               fontWeight: 'bold',
               color: '#ffffff',
               margin: '0 0 20px 0',
               lineHeight: '0.9',
               fontFamily: 'Chalkboard, sans-serif',
               textAlign: 'center',
               transform: 'rotate(2deg)'
             }}>
               <div>NOT INTO SHARING</div>
               <div>YOUR <span style={{ position: 'relative' }}>
                 ADDRESS
                 {/* Sublinhado igual ao da palavra PLAN */}
                 <div style={{
                   position: 'absolute',
                   bottom: '-8px',
                   left: 0,
                   width: '100%',
                   height: '20px',
                   background: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 200 20'%3E%3Cpath d='M8 13 L45 11 L85 13 L125 11 L165 13 L192 11' stroke='white' stroke-width='4' fill='none' stroke-linecap='round'/%3E%3C/svg%3E") no-repeat`,
                   backgroundSize: '100% 100%',
                   backgroundPosition: 'left bottom',
                   zIndex: -1,
                   filter: 'contrast(1.2) brightness(1.1)'
                 }}></div>
               </span>?</div>
             </h1>
          </div>

          {/* Mensagens de feedback - POSICIONADA NO TOPO */}
          {message && (
            <div style={{
                marginTop: '0px',
                marginBottom: '20px',
                padding: '20px',
                borderRadius: '12px',
                position: 'relative',
            backgroundColor: message.type === 'success' ? '#d4edda' : '#f8d7da',
            color: message.type === 'success' ? '#155724' : '#721c24',
            border: `1px solid ${message.type === 'success' ? '#c3e6cb' : '#f5c6cb'}`,
                fontSize: '16px',
                lineHeight: '1.5',
                textAlign: 'center',
                fontFamily: 'Coinbase Mono Light, monospace'
          }}>
            {message.text}
            <button 
              onClick={() => setMessage(null)}
              style={{
                  position: 'absolute',
                  top: '5px',
                  right: '10px',
                background: 'none',
                border: 'none',
                  fontSize: '20px',
                cursor: 'pointer',
                  color: '#ffffff',
                  padding: '4px 8px',
                  borderRadius: '4px'
              }}
            >
              ×
            </button>
          </div>
        )}

                              {/* Container dos botões com estrela */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '20px',
                      justifyContent: 'center',
                      marginLeft: '-35px'
                    }}>
                        {/* Estrela decorativa */}
            <div style={{
              flexShrink: 0,
              width: '184px',
              height: '184px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <img 
                src="/imgs/star.svg" 
                alt="Star" 
                style={{
                  width: '100%',
                  height: '100%',
                  filter: 'brightness(0) saturate(100%) invert(27%) sepia(51%) saturate(2878%) hue-rotate(346deg) brightness(104%) contrast(97%)',
                  animation: 'rotateRight 40s linear infinite'
                }}
              />
            </div>

                                  {/* Botões */}
                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px',
                        flex: 1,
                        maxWidth: '248px',
                        marginLeft: '-120px',
                        marginTop: '-3px'
                      }}>
                                                     <button
                          onClick={() => setStep("shipping")}
                          style={{
                            background: '#f9d70b',
                            border: 'none',
                            borderRadius: '0px',
                            padding: '8px 16px 4px 16px',
                            fontSize: '17px',
                            fontWeight: 'bold',
                            color: '#000000',
                            cursor: 'pointer',
                            fontFamily: 'Chalkboard, sans-serif',
                            transition: 'transform 0.2s ease',
                            whiteSpace: 'nowrap',
                            lineHeight: '1',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transform: 'rotate(-3deg)',
                            ...(window.innerWidth <= 1024 && {
                              padding: '8px 20px 4px 20px' // +4px de cada lado no mobile (2px + 2px)
                            })
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'rotate(-3deg) scale(1.05)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'rotate(-3deg) scale(1)';
                          }}
                        >
                          I'M GOOD. SEND TO MY PLACE
                        </button>

                             <button
                 onClick={() => setStep("payNo")}
                 style={{
                   background: '#f9d70b',
                   border: 'none',
                   borderRadius: '0px',
                   padding: '8px 16px 4px 16px',
                   fontSize: '17px',
                   fontWeight: 'bold',
                   color: '#000000',
                   cursor: 'pointer',
                   fontFamily: 'Chalkboard, sans-serif',
                   transition: 'transform 0.2s ease',
                   whiteSpace: 'nowrap',
                   lineHeight: '1',
                   display: 'flex',
                   alignItems: 'center',
                   justifyContent: 'center',
                   transform: 'rotate(1deg)',
                   marginTop: '5px',
                   marginLeft: '1px',
                   ...(window.innerWidth <= 1024 && {
                     padding: '8px 20px 4px 20px' // +4px de cada lado no mobile (2px + 2px)
                   })
                 }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'rotate(1deg) scale(1.05)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'rotate(1deg) scale(1)';
                }}
              >
                SEND THEM ALL TO THE PARKS
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (step === "payNo") {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.9)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
        padding: '20px'
      }}>
        {/* HUD topo global - REMOVIDO */}
        <div style={{
          position: 'relative',
          maxWidth: '500px',
          width: '100%',
          background: 'transparent',
          padding: '40px',
          borderRadius: '0px'
        }}>
          <style>{starAnimationCSS}</style>
          {/* Botão X para fechar */}
          <button
            onClick={onClose}
            style={{
              position: 'absolute',
              top: '0px',
              right: '50px',
              background: 'none',
              border: 'none',
              fontSize: '28px',
              cursor: 'pointer',
              color: '#ffffff',
              padding: '8px',
              borderRadius: '50%',
              width: '40px',
              height: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background-color 0.2s ease'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            ×
          </button>

          {/* Mensagens de feedback - POSICIONADA NO TOPO */}
        {message && (
          <div style={{
              marginTop: '0px',
              marginBottom: '20px',
            padding: '15px',
            borderRadius: '8px',
            backgroundColor: message.type === 'success' ? '#d4edda' : '#f8d7da',
            color: message.type === 'success' ? '#155724' : '#721c24',
            border: `1px solid ${message.type === 'success' ? '#c3e6cb' : '#f5c6cb'}`,
            fontSize: '14px',
              lineHeight: '1.5',
              fontFamily: 'Coinbase Mono Light, monospace',
              textAlign: 'center'
            }}>
              {message.text}
            </div>
          )}

          

          {/* Seletor de quantidade */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '20px',
            marginBottom: '30px'
          }}>
            {/* Botão menos */}
            <button
              onClick={dec}
              style={{
                width: '30px',
                height: '30px',
                background: '#f9d70b',
                border: 'none',
                borderRadius: '0px',
                fontSize: '24px',
                fontWeight: 'bold',
                color: '#000000',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontFamily: 'Arial Black, sans-serif',
                lineHeight: '1'
              }}
            >
              −
            </button>

            {/* Estrela SVG com número */}
            <div style={{
              width: '120px',
              height: '120px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative'
            }}>
                          <img 
              src="/imgs/star.svg" 
              alt="Star" 
              style={{
                width: '100%',
                height: '100%',
                filter: 'brightness(0) saturate(100%) invert(27%) sepia(51%) saturate(2878%) hue-rotate(346deg) brightness(104%) contrast(97%)',
                animation: 'rotateRight 40s linear infinite'
              }}
            />
              <span style={{
                position: 'absolute',
                color: '#ffffff',
                fontSize: '48px',
                fontWeight: 'bold',
                fontFamily: 'Arial Black, sans-serif'
              }}>
                {qty}
              </span>
            </div>

            {/* Botão mais */}
            <button 
              onClick={inc}
              style={{
                width: '30px',
                height: '30px',
                background: '#f9d70b',
                border: 'none',
                borderRadius: '0px',
                fontSize: '24px',
                fontWeight: 'bold',
                color: '#000000',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontFamily: 'Arial Black, sans-serif',
                lineHeight: '1'
              }}
            >
              +
            </button>
          </div>

          {/* Texto "we'll send X pins to the parks!" */}
          <div style={{
            textAlign: 'center',
            marginBottom: '20px',
            marginTop: '-17px',
            color: '#ffffff',
            fontSize: '18px',
            fontFamily: 'Coinbase Mono Light, monospace',
            transform: 'rotate(-3deg)'
          }}>
            <div>we'll send <span style={{ 
              color: '#f9d70b', 
              fontWeight: 'bold',
              fontFamily: 'Arial Black, sans-serif',
              fontSize: '22px',
              transform: 'rotate(8deg)',
              display: 'inline-block'
            }}>{qty}</span></div>
            <div>pins to the parks!</div>
          </div>



          {/* Botões de pagamento */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            marginBottom: '30px',
            alignItems: 'center'
          }}>
            <button
            disabled={loading === "ETH"}
              onClick={(e: React.MouseEvent) => { 
                e.preventDefault(); 
                pay("ETH", false);
              }}
              style={{
                background: '#f9d70b',
                border: 'none',
                borderRadius: '0px',
                padding: '2px 8px',
                fontSize: '28px',
                fontWeight: 'bold',
                color: '#000000',
                cursor: 'pointer',
                fontFamily: 'Chalkboard, sans-serif',
                transition: 'transform 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                lineHeight: '1',
                maxWidth: '170px',
                transform: 'rotate(-2deg)',
                marginLeft: '-75px',
                marginTop: '5px'
              }}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'rotate(-2deg) scale(1.02)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'rotate(-2deg)'}
            >
              <span style={{ marginTop: '6px' }}>
                {loading === "ETH" ? "LOADING" : "PAY IN ETH"}
              </span>
            </button>

            <button
              disabled={loading === "USDC"}
              onClick={(e: React.MouseEvent) => { 
                e.preventDefault(); 
                pay("USDC", false);
              }}
              style={{
                background: '#f9d70b',
                border: 'none',
                borderRadius: '0px',
                padding: '2px 8px',
                fontSize: '28px',
                fontWeight: 'bold',
                color: '#000000',
                cursor: 'pointer',
                fontFamily: 'Chalkboard, sans-serif',
                transition: 'transform 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                lineHeight: '1',
                maxWidth: '170px',
                transform: 'rotate(3deg)',
                marginTop: '1px',
                marginLeft: '40px',
                letterSpacing: '-0.5px',
                ...(window.innerWidth <= 1024 && {
                  padding: '2px 10px', // +2px de cada lado no mobile
                  maxWidth: '180px' // Aumenta largura para não quebrar texto
                })
              }}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'rotate(3deg) scale(1.02)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'rotate(3deg)'}
            >
              <span style={{ marginTop: '6px' }}>
                {loading === "USDC" ? "LOADING" : "PAY IN USDC"}
              </span>
            </button>
          </div>

          {/* Link para pagamento por cartão - APENAS para Mini App */}
          <div style={{
            textAlign: 'center',
            marginTop: '20px',
            marginBottom: '20px',
            padding: '0 20px'
          }}>
            <a 
              href="https://buy.stripe.com/eVq14nctFfcf5W7a6neZ200"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: '#ffffff',
                textDecoration: 'underline',
                textUnderlineOffset: '4px',
                textDecorationThickness: '2px',
                fontSize: '14px',
                fontFamily: 'Coinbase Mono Light, monospace',
                lineHeight: '1.4',
                cursor: 'pointer',
                transition: 'opacity 0.2s ease'
              }}
              onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
              onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
            >
              pay by card
            </a>
          </div>




        </div>
      </div>
    );
  }

  // shipping (YES)
  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: 'rgba(0, 0, 0, 0.85)', /* Mais escuro - aumentado de 0.7 para 0.85 */
      zIndex: 10000
    }}>
      {/* HUD topo global - REMOVIDO */}
      <div style={{
        position: 'fixed',
        left: '4%',
        right: '4%',
        top: '6dvh',
        maxHeight: '88dvh',
        background: 'transparent',
        borderRadius: '0px',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}>
        <style>{starAnimationCSS}</style>
        
        {/* Header com X (fora da área de scroll) */}
        <div style={{
          padding: '12px',
          position: 'relative',
          flexShrink: 0
        }}>
          <button
            onClick={onClose}
            style={{
              position: 'absolute',
              top: '-17px',
              right: '8px',
              background: 'none',
              border: 'none',
              fontSize: '24px',
              padding: '8px',
              cursor: 'pointer',
              color: '#ffffff',
              zIndex: 2
            }}
          >
            ×
          </button>
        </div>
        
        {/* Conteúdo rolável */}
        <div style={{
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          WebkitOverflowScrolling: 'touch',
          overscrollBehavior: 'contain',
          padding: '10px 16px 16px',
          wordBreak: 'break-word'
        }}>
          {/* Mensagens de feedback - POSICIONADA NO TOPO */}
          {message && (
            <div style={{
              marginTop: '0px',
              marginBottom: '20px',
              padding: '15px',
              borderRadius: '8px',
              position: 'relative',
              backgroundColor: message.type === 'success' ? '#d4edda' : '#f8d7da',
              color: message.type === 'success' ? '#155724' : '#721c24',
              border: `1px solid ${message.type === 'success' ? '#c3e6cb' : '#f5c6cb'}`,
              fontSize: '14px',
              lineHeight: '1.5',
              fontFamily: 'Coinbase Mono Light, monospace',
              textAlign: 'center'
            }}>
              {message.text}
            </div>
          )}



        {/* Título DELIVERY ADDRESS */}
        <div style={{
          textAlign: 'center',
          marginBottom: '20px',
          fontFamily: 'Chalkboard, sans-serif'
        }}>
          <h1 style={{
            fontSize: '32px',
            fontWeight: 'bold',
            color: '#ffffff',
            margin: '0 0 20px 0',
            lineHeight: '0.9',
            fontFamily: 'Chalkboard, sans-serif',
            textAlign: 'center',
            transform: 'rotate(2deg)'
          }}>
            <div>DELIVERY <span style={{ position: 'relative' }}>
              ADDRESS
              {/* Sublinhado igual ao da palavra PLAN */}
              <div style={{
                position: 'absolute',
                bottom: '-8px',
                left: 0,
                width: '100%',
                height: '20px',
                background: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 200 20'%3E%3Cpath d='M8 13 L45 11 L85 13 L125 11 L165 13 L192 11' stroke='white' stroke-width='4' fill='none' stroke-linecap='round'/%3E%3C/svg%3E") no-repeat`,
                backgroundSize: '100% 100%',
                backgroundPosition: 'left bottom',
                zIndex: -1,
                filter: 'contrast(1.2) brightness(1.1)'
              }}></div>
            </span></div>
          </h1>
        </div>

        {/* CSS para placeholders amarelos e autocomplete do Google */}
        <style>{`
          input::placeholder {
            color: #f9d70b !important;
            opacity: 1;
            font-family: 'Coinbase Mono Light', monospace !important;
          }
          /* Garantir que dropdown do Google Places apareça por cima do modal */
          /* O Google adiciona .pac-container diretamente no body, precisa z-index maior que o modal (10000) */
          .pac-container {
            z-index: 100001 !important;
            background-color: #1a1a1a !important;
            border: 2px solid #f9d70b !important;
            border-radius: 0 !important;
            font-family: 'Coinbase Mono Light', monospace !important;
            pointer-events: auto !important;
            touch-action: auto !important;
            -webkit-touch-callout: none !important;
            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.8) !important;
          }
          .pac-item {
            padding: 14px 10px !important;
            color: #ffffff !important;
            background-color: #1a1a1a !important;
            border-bottom: 1px solid #333 !important;
            cursor: pointer !important;
            pointer-events: auto !important;
            touch-action: manipulation !important;
            -webkit-tap-highlight-color: rgba(249, 215, 11, 0.3) !important;
            min-height: 44px !important;
            display: flex !important;
            align-items: center !important;
          }
          .pac-item:hover, .pac-item:active {
            background-color: #333 !important;
          }
          .pac-item-query {
            color: #f9d70b !important;
          }
          .pac-matched {
            color: #f9d70b !important;
            font-weight: bold !important;
          }
          .pac-icon {
            display: none !important;
          }
          /* Esconder logo do Google no dropdown */
          .pac-logo:after {
            display: none !important;
          }
          .hdpi.pac-logo:after {
            display: none !important;
          }
        `}</style>
        
        {/* Formulário */}
        <div style={{ marginBottom: '30px' }}>
          {/* Nome */}
          <input 
            placeholder="Full Name*" 
            value={addr.name}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAddr({ ...addr, name: e.target.value })}
            style={{ 
              width: '100%',
              padding: '8px',
              marginBottom: '12px',
              background: 'transparent',
              border: '2px solid #f9d70b',
              borderRadius: '0px',
              color: '#ffffff',
              fontSize: '16px',
              fontFamily: 'inherit'
            }}
          />
          <input 
            placeholder="Email*" 
            type="email" 
            value={addr.email}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAddr({ ...addr, email: e.target.value })}
            style={{ 
              width: '100%',
              padding: '8px',
              marginBottom: '12px',
              background: 'transparent',
              border: '2px solid #f9d70b',
              borderRadius: '0px',
              color: '#ffffff',
              fontSize: '16px',
              fontFamily: 'inherit'
            }}
          />
          
          {/* AddressInput com estilo personalizado */}
          <AddressInput
            value={addr.address_line1 || ''}
            onChange={(value: string) => {
              setAddr({ ...addr, address_line1: value });
            }}
            onAddressPicked={(addressData) => {
              // Processar address_components do Google Places para preencher os campos
              const components = addressData.address_components || [];
              
              const getComponent = (type: string) => {
                return components.find((c: any) => c.types?.includes?.(type));
              };
              
              const city = 
                getComponent('locality')?.long_name ||
                getComponent('postal_town')?.long_name ||
                getComponent('sublocality_level_1')?.long_name ||
                '';
              
              const region = 
                getComponent('administrative_area_level_1')?.short_name || '';
              
              const postal_code = 
                getComponent('postal_code')?.long_name || '';
              
              const country = 
                getComponent('country')?.short_name || '';
              
              // Atualizar o estado com os dados extraídos
              setAddr({
                ...addr,
                address_line1: addressData.formatted_address || addr.address_line1,
                city: city || addr.city,
                region: region || addr.region,
                postal_code: postal_code || addr.postal_code,
                country: country || addr.country,
                place_id: addressData.place_id || addr.place_id,
                lat: addressData.lat || addr.lat,
                lng: addressData.lng || addr.lng,
              });
            }}
            placeholder="Address Line 1*"
            style={{ 
              width: '100%',
              padding: '8px',
              marginBottom: '12px',
              background: 'transparent',
              border: '2px solid #f9d70b',
              borderRadius: '0px',
              color: '#ffffff',
              fontSize: '16px',
              fontFamily: 'inherit'
            }}
          />

          <input 
            placeholder="Address Line 2" 
            value={addr.address_line2}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAddr({ ...addr, address_line2: e.target.value })} 
            style={{ 
              width: '100%',
              padding: '8px',
              marginBottom: '12px',
              background: 'transparent',
              border: '2px solid #f9d70b',
              borderRadius: '0px',
              color: '#ffffff',
              fontSize: '16px',
              fontFamily: 'inherit'
            }}
          />

                    {/* Cidade e Estado */}
          <div style={{ display: 'flex', gap: '16px', marginBottom: '12px', width: '100%' }}>
            <input 
              placeholder="City*" 
              value={addr.city}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAddr({ ...addr, city: e.target.value })}
              style={{ 
                width: 'calc(65% - 8px)',
                padding: '8px',
                background: 'transparent',
                border: '2px solid #f9d70b',
                borderRadius: '0px',
                color: '#ffffff',
                fontSize: '16px',
                fontFamily: 'inherit'
              }}
            />
            <input 
              placeholder="State*" 
              value={addr.region}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAddr({ ...addr, region: e.target.value })}
              style={{ 
                width: 'calc(35% - 8px)',
                padding: '8px',
                background: 'transparent',
                border: '2px solid #f9d70b',
                borderRadius: '0px',
                color: '#ffffff',
                fontSize: '16px',
                fontFamily: 'inherit'
              }}
            />
          </div>

          {/* CEP e País */}
          <div style={{ display: 'flex', gap: '16px', marginBottom: '12px', width: '100%' }}>
            <input 
              placeholder="ZIP*" 
              value={addr.postal_code}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAddr({ ...addr, postal_code: e.target.value })}
              style={{ 
                width: 'calc(50% - 8px)',
                padding: '8px',
                background: 'transparent',
                border: '2px solid #f9d70b',
                borderRadius: '0px',
                color: '#ffffff',
                fontSize: '16px',
                fontFamily: 'inherit'
              }}
            />
            <input 
              placeholder="Country*" 
              value={addr.country}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const val = e.target.value;
                // Primeira letra maiúscula, resto normal
                const formatted = val.charAt(0).toUpperCase() + val.slice(1);
                setAddr({ ...addr, country: formatted });
              }}
              style={{ 
                width: 'calc(50% - 8px)',
                padding: '8px',
                background: 'transparent',
                border: '2px solid #f9d70b',
                borderRadius: '0px',
                color: '#ffffff',
                fontSize: '16px',
                fontFamily: 'inherit'
              }}
            />
          </div>
        </div>

        {/* Seletor de quantidade com estrela */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '20px',
          marginBottom: '30px'
        }}>
          {/* Botão menos */}
          <button
            onClick={dec}
            style={{
              width: '24px',
              height: '24px',
              background: '#f9d70b',
              border: 'none',
              borderRadius: '0px',
              fontSize: '20px',
              fontWeight: 'bold',
              color: '#000000',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontFamily: 'Arial Black, sans-serif',
              lineHeight: '1',
              marginTop: '6px',
              alignSelf: 'center'
            }}
          >
            −
          </button>

          {/* Estrela SVG com número */}
          <div style={{
            width: '96px',
            height: '96px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative'
          }}>
            <img 
              src="/imgs/star.svg" 
              alt="Star" 
              style={{
                width: '100%',
                height: '100%',
                filter: 'brightness(0) saturate(100%) invert(27%) sepia(51%) saturate(2878%) hue-rotate(346deg) brightness(104%) contrast(97%)',
                animation: 'rotateRight 40s linear infinite'
              }}
            />
            <span style={{
              position: 'absolute',
              color: '#ffffff',
              fontSize: '38px',
              fontWeight: 'bold',
              fontFamily: 'Arial Black, sans-serif'
            }}>
              {qty}
            </span>
          </div>

          {/* Botão mais */}
          <button
            onClick={inc}
            style={{
              width: '24px',
              height: '24px',
              background: '#f9d70b',
              border: 'none',
              borderRadius: '0px',
              fontSize: '20px',
              fontWeight: 'bold',
              color: '#000000',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontFamily: 'Arial Black, sans-serif',
              lineHeight: '1',
              marginTop: '6px',
              alignSelf: 'center'
            }}
          >
            +
          </button>
        </div>

        {/* Botões de pagamento */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          marginBottom: '30px',
          alignItems: 'center'
        }}>
          <button
            disabled={loading === "ETH"}
              onClick={(e: React.MouseEvent) => { 
                e.preventDefault(); 
                pay("ETH", true);
              }}
            style={{
              background: '#f9d70b',
              border: 'none',
              borderRadius: '0px',
              padding: '2px 8px',
              fontSize: '28px',
              fontWeight: 'bold',
              color: '#000000',
              cursor: 'pointer',
              fontFamily: 'Chalkboard, sans-serif',
              transition: 'transform 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              lineHeight: '1',
              maxWidth: '170px',
              transform: 'rotate(-2deg)',
              marginLeft: '-75px',
              marginTop: '5px'
            }}
            onMouseEnter={(e) => e.currentTarget.style.transform = 'rotate(-2deg) scale(1.02)'}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'rotate(-2deg)'}
          >
            <span style={{ marginTop: '6px' }}>
              {loading === "ETH" ? "LOADING" : "PAY IN ETH"}
            </span>
          </button>

          <button
            disabled={loading === "USDC"}
              onClick={(e: React.MouseEvent) => { 
                e.preventDefault(); 
                pay("USDC", true);
              }}
            style={{
              background: '#f9d70b',
              border: 'none',
              borderRadius: '0px',
              padding: '2px 8px',
              fontSize: '28px',
              fontWeight: 'bold',
              color: '#000000',
              cursor: 'pointer',
              fontFamily: 'Chalkboard, sans-serif',
              transition: 'transform 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              lineHeight: '1',
              maxWidth: '170px',
              transform: 'rotate(3deg)',
              marginLeft: '75px',
              marginTop: '-5px',
              letterSpacing: '-0.5px'
            }}
            onMouseEnter={(e) => e.currentTarget.style.transform = 'rotate(3deg) scale(1.02)'}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'rotate(3deg)'}
          >
            <span style={{ marginTop: '6px' }}>
              {loading === "USDC" ? "LOADING" : "PAY IN USDC"}
            </span>
          </button>
        </div>

        {/* Link para pagamento por cartão - APENAS para Mini App */}
        <div style={{
          textAlign: 'center',
          marginTop: '20px',
          marginBottom: '20px',
          padding: '0 20px'
        }}>
          <a 
            href="https://buy.stripe.com/eVq14nctFfcf5W7a6neZ200"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: '#ffffff',
              textDecoration: 'none',
              fontSize: '14px',
              fontFamily: 'Coinbase Mono Light, monospace',
              lineHeight: '1.4',
              cursor: 'pointer',
              transition: 'opacity 0.2s ease'
            }}
            onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
            onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
          >
            pay by card? skip the form and <span style={{ textDecoration: 'underline', textUnderlineOffset: '4px', textDecorationThickness: '2px' }}>just click here</span>
          </a>
        </div>
        </div>


      </div>



    </div>
  );
}


